// See EnrollHostsDrawer.spec.md for EHD-FR-006
import { makeStyles } from '@material-ui/styles'
import Alert from 'core/components/Alert'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
import type { IResmgrHostSelector } from 'openstack/components/infrastructure/model'
import generateTestId from 'utils/test-helpers'
import type { EnrollStepProps } from './EnrollHostsDrawer'
import type { EnrollmentSelection } from './model'
import StepSection from './StepSection'

// The lead wires onEdit to StepperModalForm's stepRef.goToStep when this step is connected
// (T015); it is not yet part of the shared EnrollStepProps.
export interface SummaryStepProps extends EnrollStepProps {
  onEdit: (stepIndex: number) => void
}

export interface SummaryRow {
  label: string
  value: string
  editStep?: number
}

// Hosts (Hypervisor first), then the rest of step 4's roles, or "None" if nothing was picked.
const rolesValue = (selection: EnrollmentSelection): string => {
  const roles = [
    selection.hypervisor && 'Hypervisor',
    selection.imageLibrary && 'VM Image Library',
    selection.remoteSupport && 'Advanced Remote Support',
    selection.dns && 'DNS',
  ].filter(Boolean) as string[]
  return roles.length > 0 ? roles.join(', ') : 'None'
}

// EHD-FR-006: the rows step 5 shows, each with the step index to jump back to for an edit.
// Hosts has no editStep since there is no step to pick hosts from.
export const summaryRows = (
  selection: EnrollmentSelection,
  hosts: IResmgrHostSelector[],
): SummaryRow[] => [
  {
    label: 'Hosts',
    value: hosts.map((host) => host.info?.hostname || host.id).join(', '),
  },
  {
    label: 'Cluster',
    value: selection.clusterName || 'None',
    editStep: 0,
  },
  {
    label: 'Network',
    value: selection.hostConfigName || 'None',
    editStep: 1,
  },
  {
    label: 'Volume backends',
    value: selection.backends.length > 0 ? selection.backends.join(', ') : 'None',
    editStep: 2,
  },
  {
    label: 'Roles',
    value: rolesValue(selection),
    editStep: 3,
  },
]

// EHD-FR-006: exact copy required by the spec, kept as one constant so the test can assert it
const bannerText =
  "Roles are applied first, then each host's storage connection is checked. " +
  "A failed check doesn't undo the roles."

// Step 5: a read only review of every choice made in steps 1 to 4, each with a link back to
// its step so the user can edit it without losing the rest of the selection.
export default function SummaryStep({ selection, hosts, onEdit }: SummaryStepProps) {
  const classes = useStyles()
  const rows = summaryRows(selection, hosts)

  return (
    <div className={classes.root}>
      {/* Figma: grey header and the rows sit in one card */}
      <div className={classes.summaryCard}>
        <StepSection
          className={classes.summaryHeader}
          title="Summary"
          description="Review the configuration before applying it."
        />
        <div className={classes.rows}>
          {rows.map((row) => (
            <div key={row.label} className={classes.row}>
              <Text variant="body2" className={classes.label}>
                {row.label}:
              </Text>
              <Text
                variant="caption1"
                className={classes.value}
                data-testid={generateTestId('summary', row.label, 'value')}
              >
                {row.value}
              </Text>
              {row.editStep !== undefined && (
                <button
                  type="button"
                  className={classes.editButton}
                  aria-label={`Edit ${row.label}`}
                  onClick={() => onEdit(row.editStep)}
                >
                  Edit
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      <Alert
        variant="primary"
        message={
          <span className={classes.bannerMessage}>
            <FontAwesomeIcon className={classes.bannerIcon}>circle-info</FontAwesomeIcon>
            <Text variant="body2" component="span" className={classes.bannerText}>
              {bannerText}
            </Text>
          </span>
        }
      />
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  summaryCard: {
    border: `1px solid ${colors.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  // The header's own border is replaced by the outer card border plus a line under it
  summaryHeader: {
    border: 0,
    borderRadius: 0,
    borderBottom: `1px solid ${colors.grey[200]}`,
  },
  rows: {
    display: 'grid',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 4),
  },
  // Figma: label column, bold value, Edit link on the right; no lines between rows
  row: {
    display: 'grid',
    gridTemplateColumns: '30% 1fr auto',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  label: {
    color: colors.grey[700],
  },
  value: {
    color: colors.grey[700],
  },
  editButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: colors.blue[700],
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
  },
  bannerMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  bannerIcon: {
    color: colors.blue[900],
    fontSize: 20,
  },
  bannerText: {
    color: colors.blue[900],
    fontWeight: 600,
  },
}))
